import React, { useState } from 'react';
import SimulationCard from './SimulationCard';
import { OmbudsmanSimulation } from '../../../../types/workspace';
import { Badge } from '../../../ui/badge';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Layers } from 'lucide-react';

interface SimulationStackProps {
  simulations: OmbudsmanSimulation[];
  onOpen?: (id: string) => void;
  onExportPDF?: (id: string) => void;
  onDownloadJSON?: (id: string) => void;
  onArchive?: (id: string) => void;
}

const SimulationStack: React.FC<SimulationStackProps> = ({
  simulations,
  onOpen,
  onExportPDF,
  onDownloadJSON,
  onArchive,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (simulations.length === 0) return null;

  // Most recent simulation on top
  const topSimulation = simulations[0];
  const stackCount = simulations.length;

  return (
    <div className="relative">
      {/* Enhanced stack depth indicators with scale and rotation */}
      {stackCount > 1 && (
        <>
          {/* Second card layer */}
          <div
            className="absolute top-0 left-0 right-0 h-full bg-white border border-gray-200 rounded-xl shadow-sm"
            style={{
              transform: 'translateY(-6px) translateX(4px) scale(0.97) rotate(0.5deg)',
              zIndex: -1,
            }}
          />
          {/* Third card layer */}
          {stackCount > 2 && (
            <div
              className="absolute top-0 left-0 right-0 h-full bg-white border border-gray-200 rounded-xl shadow-sm"
              style={{
                transform: 'translateY(-12px) translateX(8px) scale(0.94) rotate(1deg)',
                zIndex: -2,
              }}
            />
          )}
        </>
      )}

      {/* Top card (most recent) with stack badge */}
      <div className="relative" style={{ zIndex: 1 }}>
        <SimulationCard
          simulation={topSimulation}
          onOpen={onOpen}
          onExportPDF={onExportPDF}
          onDownloadJSON={onDownloadJSON}
          onArchive={onArchive}
        />

        {/* Stack badge overlay - positioned on the card */}
        {stackCount > 1 && (
          <div className="absolute bottom-16 left-6 z-20">
            <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700 shadow-sm">
              <div className="flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5" />
                <span className="font-medium">{stackCount} cases</span>
              </div>
            </Badge>
          </div>
        )}
      </div>

      {/* Expand/Collapse button */}
      {stackCount > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="mt-3 w-full text-sm text-primary hover:text-primary/80 font-medium flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-gray-50 transition-all duration-200"
        >
          {expanded ? (
            <>
              <ChevronUpIcon className="w-4 h-4" />
              Show less
            </>
          ) : (
            <>
              <ChevronDownIcon className="w-4 h-4" />
              Show {stackCount - 1} more simulation{stackCount - 1 !== 1 ? 's' : ''}
            </>
          )}
        </button>
      )}

      {/* Expanded state shows all simulations with slide-in animation */}
      {expanded && stackCount > 1 && (
        <div className="mt-4 space-y-4 pl-6 border-l-2 border-primary/20 animate-in slide-in-from-top-2 duration-200">
          {simulations.slice(1).map((sim, index) => (
            <div
              key={sim.id}
              className="animate-in fade-in slide-in-from-top-1 duration-200"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <SimulationCard
                simulation={sim}
                onOpen={onOpen}
                onExportPDF={onExportPDF}
                onDownloadJSON={onDownloadJSON}
                onArchive={onArchive}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SimulationStack;
